import { type DeliveryNote } from '~/types/deliveryNote';
import {
  type ConcreteProduct,
  type LineItem,
} from '~/types/deliveryNoteLineItem';

import { type FormDeliveryNoteFormFields } from '~/modules/ai-parser-review/components/FormDeliveryNote';

import { getNestedProperty, isObjectType } from '@vestigas/utils';
import { datetime4server, formatAsUTCDate, parseNumber } from '~/utils';

import {
  parseNumericValueToFloat,
  setNestedValue,
  updateNumericField,
} from './utils';

export const updateLineItemsData = (
  draft: DeliveryNote,
  formData: FormDeliveryNoteFormFields,
) => {
  if (!draft.transaction?.logisticsPackage?.[0]?.lineItem) {
    return;
  }

  // Replace the entire line items data to handle additions / changes / reorderings / deletions.
  // Then process properties of the line items where required.
  draft.transaction.logisticsPackage[0].lineItem =
    formData.lineItems as LineItem[];

  for (const [index, formLineItem] of formData.lineItems.entries()) {
    const draftLineItem =
      draft.transaction.logisticsPackage[0]?.lineItem[index];
    if (!draftLineItem || !formLineItem) {
      continue;
    }

    updateLineItemWeightAndQuantity(draftLineItem, formLineItem);

    updateLineItemConcreteData(draftLineItem, formLineItem);
  }
};

const updateLineItemWeightAndQuantity = (
  draftLineItem: LineItem,
  formLineItem: FormDeliveryNoteFormFields['lineItems'][0],
) => {
  if (formLineItem.delivery?.netWeight?.weight) {
    const parsedWeight = parseNumber(formLineItem.delivery.netWeight.weight);
    const lacksNetWeight = Number.isNaN(parsedWeight);

    draftLineItem.delivery.netWeight.weight = lacksNetWeight
      ? undefined
      : parsedWeight;

    if (lacksNetWeight) {
      // Remove entire netWeight object if it lacks a weight value
      draftLineItem.delivery.netWeight = undefined;
    }
  } else {
    // Remove entire netWeight object if it lacks a weight value
    draftLineItem.delivery.netWeight = undefined;
  }

  const parsedQuantity = parseNumber(
    formLineItem.delivery.productUnit.quantity,
  );
  draftLineItem.delivery.productUnit.quantity = Number.isNaN(parsedQuantity)
    ? 0
    : parsedQuantity;

  for (const key of [
    'requestedDelivery',
    'actualDelivery',
    'remainingDelivery',
  ] as const) {
    updateNumericField(
      formLineItem.delivery[key],
      true,
      draftLineItem.delivery,
      key,
    );
  }

  if (formLineItem.documentLine?.note?.content?.grossWeight?.weight) {
    const parsedWeight = parseNumber(
      formLineItem.documentLine.note.content.grossWeight.weight,
    );
    const lacksGrossWeight = Number.isNaN(parsedWeight);

    draftLineItem.documentLine.note.content.grossWeight.weight =
      lacksGrossWeight ? undefined : parsedWeight;

    if (formLineItem.documentLine?.note?.content?.grossWeight?.date) {
      draftLineItem.documentLine.note.content.grossWeight.date =
        formatAsUTCDate(
          formLineItem.documentLine?.note?.content?.grossWeight?.date,
          datetime4server,
        );
    }

    if (lacksGrossWeight) {
      // Remove entire grossWeight object if it lacks a weight value
      draftLineItem.documentLine.note.content.grossWeight = undefined;
    }
  } else {
    // Remove entire grossWeight object if it lacks a weight value
    draftLineItem.documentLine.note.content.grossWeight = undefined;
  }

  if (formLineItem.documentLine?.note?.content?.tareWeight?.weight) {
    const parsedWeight = parseNumber(
      formLineItem.documentLine.note.content.tareWeight.weight,
    );
    const lacksTareWeight = Number.isNaN(parsedWeight);

    draftLineItem.documentLine.note.content.tareWeight.weight = lacksTareWeight
      ? undefined
      : parsedWeight;

    if (formLineItem.documentLine?.note?.content?.tareWeight?.date) {
      draftLineItem.documentLine.note.content.tareWeight.date = formatAsUTCDate(
        formLineItem.documentLine?.note?.content?.tareWeight?.date,
        datetime4server,
      );
    }

    if (lacksTareWeight) {
      // Remove entire tareWeight object if it lacks a weight value
      draftLineItem.documentLine.note.content.tareWeight = undefined;
    }
  } else {
    // Remove entire tareWeight object if it lacks a weight value
    draftLineItem.documentLine.note.content.tareWeight = undefined;
  }
};

const updateLineItemConcreteData = (
  draftLineItem: LineItem,
  formLineItem: FormDeliveryNoteFormFields['lineItems'][0],
) => {
  if (!formLineItem?.product?.concrete) {
    return;
  }

  const draftConcrete = draftLineItem.product.concrete;
  const formDataConcrete = formLineItem.product.concrete;

  if (formDataConcrete.lastValidation) {
    draftConcrete.lastValidation = formatAsUTCDate(
      formDataConcrete.lastValidation,
      datetime4server,
    );
  }

  // Update numeric concrete fields
  const concreteFields = [
    { path: 'processabilityTime' },
    { path: 'waterCementRatio' },
  ];

  for (const { path } of concreteFields) {
    updateNestedConcreteValue(draftConcrete, formDataConcrete, path);
  }

  if (formDataConcrete.chlorideContent?.value) {
    const parsedChlorideContent = parseNumber(
      formDataConcrete.chlorideContent?.value,
    );
    const lacksChlorideContent = Number.isNaN(parsedChlorideContent);

    draftConcrete.chlorideContent.value = lacksChlorideContent
      ? undefined
      : parsedChlorideContent;

    if (lacksChlorideContent) {
      // Remove entire chlorideContent object if it lacks a value
      draftConcrete.chlorideContent = undefined;
    }
  } else {
    // Remove entire chlorideContent object if it lacks a value
    draftConcrete.chlorideContent = undefined;
  }

  if (formDataConcrete.returnedMaterial?.value) {
    const parsedReturnedMaterial = parseNumber(
      formDataConcrete.returnedMaterial?.value,
    );
    const lacksReturnedMaterial = Number.isNaN(parsedReturnedMaterial);

    draftConcrete.returnedMaterial.value = lacksReturnedMaterial
      ? undefined
      : parsedReturnedMaterial;

    if (lacksReturnedMaterial) {
      // Remove entire returnedMaterial object if it lacks a value
      draftConcrete.returnedMaterial = undefined;
    }
  } else {
    // Remove entire returnedMaterial object if it lacks a value
    draftConcrete.returnedMaterial = undefined;
  }

  updateConcreteComponents(draftConcrete, formDataConcrete);
};

const updateNestedConcreteValue = (
  draftConcrete: any,
  formDataConcrete: any,
  path: string,
) => {
  const value = parseNumericValueToFloat(
    getNestedProperty(formDataConcrete, path),
  );
  const lacksValue = Number.isNaN(value) || value === undefined;

  if (path === 'returnedMaterial.value' && lacksValue) {
    // Remove entire returnedMaterial object if it lacks a value
    setNestedValue(draftConcrete, ['returnedMaterial'], undefined);
    return;
  }

  const target = path.split('.');

  setNestedValue(draftConcrete, target, value);
};

const updateConcreteComponents = (
  draftConcrete: ConcreteProduct,
  formDataConcrete: ConcreteProduct,
) => {
  if (
    !formDataConcrete.components?.length ||
    !draftConcrete.components?.length
  ) {
    return;
  }

  for (const [index, component] of draftConcrete.components.entries()) {
    const formComponent = formDataConcrete.components[index];
    if (!formComponent) {
      continue;
    }

    updateNumericField(formComponent.quantity, true, component, 'quantity');
    updateNumericField(formComponent.requested, true, component, 'requested');
  }

  // Remove empty concrete components (basic implementation - does not handle nested objects etc.)
  draftConcrete.components = draftConcrete.components.filter((component) => {
    return Object.values(component).some((value) => {
      if (isObjectType(value)) {
        return false;
      }

      return ![undefined, null, ''].includes(value);
    });
  });
};
